import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SystemNotification } from '../dto';
import { AbstractService } from './abstractservice';
import { ApplicationService } from './application.service';
import { InventoryService } from './inventory.service';

// Durable, best-effort notification queue (see
// Docs/CritterServer/System-Notifications-Architecture.md) - backs the mailbox's Alerts tab and
// the envelope's unread badge. A notification's presence in pending$ means it's unacked; there's
// no separate read/unread flag, mirroring the server's own row-existence-is-the-state model.
@Injectable({
    providedIn: 'root'
})
export class SystemNotificationService extends AbstractService {

    public pending$: BehaviorSubject<SystemNotification[]> = new BehaviorSubject<SystemNotification[]>([]);

    constructor(private http: HttpClient, private coreAppService: ApplicationService, private inventoryService: InventoryService) {
        super(coreAppService.authService, coreAppService.featureFlagService);

        // One delivery attempt per call (server-side: SystemNotificationService.FetchAndAdvance
        // bumps every pending row's tries) - deliberately the ONLY trigger, not also a separate
        // activeUser$-on-login subscription: NotificationService's own activeUser$ handler
        // already calls SignalService.initializeConnection() on every login, which always fires
        // reconnected$ once connected - a second independent login-trigger here would fire twice
        // per real login and burn through the tries budget twice as fast for nothing. reconnected$
        // covers both the first-ever connect and a later recovered drop in one mechanism.
        coreAppService.notificationService.reconnected$.subscribe(() => {
            if (this.authService.isAuthenticated()) {
                this.fetchAndAdvance();
            }
        });
    }

    private fetchAndAdvance(): void {
        this.http.get<SystemNotification[]>(`${environment.apiUrl}/systemNotifications`, this.httpOptionsAuthJson())
            .pipe(
                map(rows => rows.map(r => new SystemNotification(r, this.coreAppService.jsonMapper.jsonEnricher()))),
                catchError(this.handleError.bind(this))
            )
            .subscribe(rows => {
                this.pending$.next(rows);

                // Anything that shows up here already had its effects applied server-side in the
                // past - unlike a live SignalR push, the client's already-loaded cash/inventory
                // may or may not reflect that yet (a fresh login already does; a SignalR
                // reconnect after a drop mid-session does not, since that path never re-fetches
                // user/inventory state on its own). Resync both once per batch here rather than
                // patching local state again when each notification is later opened - that's what
                // used to double-apply a cash/item change into a "mirage" that only cleared on a
                // manual refresh (RandomEventService.openAndAck's applyLocally=false skips that
                // patch for exactly this reason).
                if (rows.length > 0) {
                    this.coreAppService.authService.refreshSession().subscribe();
                    this.inventoryService.getInventory().subscribe(inv => this.inventoryService.setInventory(inv));
                }
            });
    }

    // Fire-and-forget by design - the caller (RandomEventService.openAndAck) has already shown
    // the notification to the player by the time this is called, so there's nothing useful to
    // roll back to on failure; a lost ack just means the row survives for one more delivery
    // attempt, which the tries-until-give-up mechanism already tolerates.
    public ack(id: string): void {
        this.http.post(`${environment.apiUrl}/systemNotifications/${id}/ack`, null, this.httpOptionsAuthJson())
            .pipe(catchError(this.handleError.bind(this)))
            .subscribe();

        this.pending$.next(this.pending$.getValue().filter(n => n.Id !== id));
    }
}
